import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back to Aurum.");
      nav(u.onboarded || u.role === "admin" ? "/app" : "/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--bg)] text-[var(--text)]">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden grain border-r border-[var(--border2)]"
        style={{ background: "radial-gradient(circle at 20% 20%, rgba(214,174,87,0.10), transparent 55%), #0D1117" }}>
        <Link to="/"><Logo size={30} /></Link>
        <div>
          <h2 className="text-3xl font-bold tracking-tight max-w-sm">Trade the plan. Not the emotion.</h2>
          <p className="mt-4 text-[var(--text-sec)] max-w-sm">
            Aurum monitors XAU/USD against your rules, calculates risk before action, and tells
            you when to wait. Intelligence before execution.
          </p>
        </div>
        <p className="text-xs text-[var(--text-muted)]">Trading involves substantial risk. Aurum provides analysis, not financial advice.</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="text-2xl font-bold tracking-tight">Log in</h1>
          <p className="mt-1 text-sm text-[var(--text-sec)]">Access your trading terminal.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm text-[var(--text-sec)]">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2.5 text-sm outline-none focus:border-aurum transition-colors" />
            </div>
            <div>
              <label className="text-sm text-[var(--text-sec)]">Password</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2.5 text-sm outline-none focus:border-aurum transition-colors" />
            </div>
            <button data-testid="login-submit" disabled={loading}
              className="btn-gold w-full rounded-lg py-2.5 text-sm font-semibold transition-all hover:brightness-110 disabled:opacity-60">
              {loading ? "Signing in…" : "Log in"}
            </button>
          </form>
          <p className="mt-6 text-sm text-[var(--text-sec)]">
            No account? <Link to="/signup" className="text-aurum hover:underline" data-testid="to-signup">Start paper trading</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
